import FormHeader from "@/components/FormHeader";
import InputError from "@/components/InputError";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import useSessionStorage from "@/hooks/useSessionStorage";
import api from "@/lib/axios";
import { DataBerkas, SysBerkas } from "@/types";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { FileText, Info, Loader2, UploadCloud } from "lucide-react";
import { useState } from "react";
import toast from "react-hot-toast";
import { LuUpload } from "react-icons/lu";
import { RiDeleteBack2Line } from "react-icons/ri";

// ... (postData dan deleteData tetap sama)
const postData = async (newData: FormData) => {
  const id_sys_periode = newData.get("id_sys_periode");
  const response = await api.post(
    `/siswa/daftar-kembali/data-berkas/upload-sanggah-berkas?id_sys_periode=${id_sys_periode}`,
    newData,
    {
      headers: { "Content-Type": "multipart/form-data" },
    },
  );
  return response.data;
};

const deleteData = async (newData: FormData) => {
  const id_data_berkas_siswa = newData.get("id_data_berkas_siswa");
  const id_sys_periode = newData.get("id_sys_periode");

  const response = await api({
    url: "/siswa/daftar-kembali/data-berkas/delete",
    method: "DELETE",
    data: { id_data_berkas_siswa, id_sys_periode },
  });
  return response.data;
};

export default function Form({
  method,
  closeModal,
  selectedRow,
  selectedBerkas,
  idDataPendaftaran,
}: {
  method: "POST" | "PATCH" | "DELETE";
  closeModal: () => void;
  selectedRow?: SysBerkas | null;
  selectedBerkas: DataBerkas | null;
  idDataPendaftaran?: string;
}) {
  const [sessionPeriode] = useSessionStorage("sessionPeriode", {
    id_sys_periode: "",
  });

  const [form, setForm] = useState<DataBerkas>({
    id_data_berkas_siswa: "",
    id_data_pendaftaran: idDataPendaftaran || "",
    id_sys_berkas: String(selectedRow?.id_sys_berkas) || "",
    nomor: selectedBerkas?.nomor || "",
  });
  const [file, setFile] = useState<File | null>(null);

  const isPost = method === "POST";
  const isDelete = method === "DELETE";

  const queryClient = useQueryClient();
  const { mutate, isPending, error } = useMutation({
    mutationFn: isPost ? postData : isDelete ? deleteData : undefined,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["listDataBerkas"] });
      queryClient.invalidateQueries({ queryKey: ["dataBerkasInvalid"] });
      queryClient.invalidateQueries({ queryKey: ["dataStep"] });
      closeModal();
      toast.success(data?.message || "Success.");
    },
    onError: (
      error: AxiosError<{
        message?: string;
        errors?: Record<string, string[]>;
      }>,
    ) => {
      const err = error as AxiosError<{
        message?: string;
        errors?: Record<string, string[]>;
      }>;
      const errorMessage =
        err.response?.data?.message || "An unexpected error occurred.";
      toast.error(errorMessage);
    },
  });

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files && e.target.files[0];
    if (file) setFile(file);
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    event.stopPropagation();

    const formData = new FormData();

    if (isDelete) {
      formData.append(
        "id_data_berkas_siswa",
        selectedBerkas?.id_data_berkas_siswa || "",
      );
    }

    formData.append("id_data_pendaftaran", form.id_data_pendaftaran);
    formData.append("id_sys_berkas", form.id_sys_berkas);
    formData.append("nomor", form.nomor || "");

    if (file) formData.append("file_berkas", file);
    if (sessionPeriode?.id_sys_periode)
      formData.append("id_sys_periode", sessionPeriode?.id_sys_periode);

    mutate(formData);
  };

  // ==============================
  // UI: DELETE FORM
  // ==============================
  if (isDelete) {
    return (
      <form onSubmit={handleSubmit} className="flex flex-col gap-6">
        <FormHeader
          icon={<RiDeleteBack2Line className="text-red-500" />}
          title="Hapus Data Berkas"
          description="Tindakan ini tidak dapat dibatalkan."
        />

        <div className="bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-900/50 p-4 rounded-xl flex items-start gap-3">
          <Info className="w-5 h-5 text-red-500 mt-0.5 shrink-0" />
          <div>
            <p className="text-sm text-red-800 dark:text-red-200">
              Apakah Anda yakin ingin menghapus berkas berikut?
            </p>
            <p className="font-semibold text-red-900 dark:text-red-100 mt-1">
              "{selectedBerkas?.sys_berkas?.jenis_berkas?.nama_jenis_berkas}"
            </p>
          </div>
        </div>

        <div className="flex justify-end gap-3 pt-2">
          <Button
            type="button"
            variant="outline"
            disabled={isPending}
            onClick={closeModal}
          >
            Batal
          </Button>
          <Button
            variant="destructive"
            className="flex items-center gap-2"
            disabled={isPending}
          >
            {isPending ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" /> Menghapus...
              </>
            ) : (
              "Ya, Hapus Berkas"
            )}
          </Button>
        </div>
      </form>
    );
  }

  // ==============================
  // UI: UPLOAD FORM
  // ==============================
  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-6">
      <FormHeader
        icon={<LuUpload className="text-blue-500" />}
        title={`Upload ${selectedRow?.jenis_berkas?.nama_jenis_berkas}`}
        description={
          selectedRow?.keterangan_berkas ||
          "Pastikan file yang diunggah sesuai dengan ketentuan."
        }
      />

      <section className="space-y-6">
        {/* AREA UNGGAH FILE (DROPZONE STYLE) */}
        <div className="space-y-3">
          <Label className="text-slate-700 dark:text-slate-300">
            Pilih File Berkas
          </Label>
          <div className="relative group flex flex-col items-center justify-center w-full h-36 border-2 border-dashed border-slate-300 dark:border-slate-700 rounded-2xl bg-slate-50 dark:bg-slate-900/50 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors overflow-hidden">
            <Input
              type="file"
              accept=".pdf, image/*"
              onChange={handleFileChange}
              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
            />

            {file ? (
              <div className="flex flex-col items-center text-center p-4">
                <FileText className="w-8 h-8 text-blue-500 mb-2" />
                <p className="text-sm font-semibold text-slate-800 dark:text-slate-200 line-clamp-1">
                  {file.name}
                </p>
                <p className="text-xs text-slate-500 mt-1">
                  {(file.size / 1024).toFixed(1)} KB
                </p>
              </div>
            ) : (
              <div className="flex flex-col items-center text-center p-4 pointer-events-none">
                <div className="w-12 h-12 bg-white dark:bg-slate-800 rounded-full shadow-sm flex items-center justify-center mb-3">
                  <UploadCloud className="w-6 h-6 text-slate-400 group-hover:text-blue-500 transition-colors" />
                </div>
                <p className="text-sm font-medium text-slate-700 dark:text-slate-300">
                  Klik atau seret file ke area ini
                </p>
              </div>
            )}
          </div>

          {/* INFORMASI KETENTUAN FILE */}
          <div className="flex flex-wrap items-center gap-2 bg-slate-50 dark:bg-slate-900/50 p-3 rounded-xl border border-slate-100 dark:border-slate-800">
            <Info className="w-4 h-4 text-slate-400" />
            <span className="text-xs text-slate-500 mr-2">Ketentuan:</span>

            {selectedRow?.extension_allowed?.map((value: string) => (
              <Badge
                key={value}
                variant="secondary"
                className="text-[10px] px-2 py-0 h-5"
              >
                .{value}
              </Badge>
            ))}

            {selectedRow?.max_upload && (
              <Badge
                variant="outline"
                className="text-[10px] px-2 py-0 h-5 border-slate-200 dark:border-slate-700"
              >
                Maks. {selectedRow.max_upload} KB
              </Badge>
            )}
          </div>
        </div>

        {/* INPUT NOMOR TAMBAHAN */}
        {selectedRow?.jenis_berkas?.is_add_filed_tambahan ? (
          <div className="space-y-2 bg-blue-50/50 dark:bg-blue-950/20 p-4 rounded-xl border border-blue-100 dark:border-blue-900/50">
            <Label className="text-slate-700 dark:text-slate-300 flex flex-col gap-1">
              <span>Nomor {selectedRow?.jenis_berkas?.nama_jenis_berkas}</span>
              {selectedRow?.jenis_berkas?.slug_jenis_berkas === "DTKS" && (
                <span className="text-xs font-normal text-blue-600 dark:text-blue-400">
                  *Jika tidak ada nomor, masukkan NIK / nama penerima.
                </span>
              )}
            </Label>
            <Input
              required
              placeholder="Ketik nomor dokumen di sini..."
              value={form.nomor}
              className="bg-white dark:bg-slate-950"
              onChange={(event) =>
                setForm((prev) => ({ ...prev, nomor: event.target.value }))
              }
            />
            <InputError
              text={error?.response?.data?.errors?.nomor?.join(",")}
            />
          </div>
        ) : (
          ""
        )}
      </section>

      {/* ACTION BUTTONS */}
      <div className="flex justify-end gap-3 pt-4 mt-2 border-t border-slate-100 dark:border-slate-800">
        <Button
          type="button"
          variant="ghost"
          onClick={closeModal}
          disabled={isPending}
        >
          Batal
        </Button>
        <Button
          className="flex items-center gap-2"
          disabled={isPending || (!file && isPost)}
        >
          {isPending ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" /> Menyimpan...
            </>
          ) : (
            "Simpan Berkas"
          )}
        </Button>
      </div>
    </form>
  );
}
